import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { css } from "styled-system/css";
import { Ledger } from "~/components/charts";
import { api } from "~/lib/api";
import type {
  RepoAuditResponse,
  RepoAuditRow,
  RepoAuditBucket,
  RepoAuditCheck,
  RepoAuditStatus,
} from "@aura/shared";
import { repoDek, repoHeadline } from "../story";
import { fmtFixed } from "~/lib/format";
import { ed, btn, field, tbl } from "~/components/editorial";

type SortKey = "score" | "name" | "lastPushedAt";
type BucketFilter = "all" | RepoAuditBucket;

const BUCKET_LABEL: Record<RepoAuditBucket, string> = {
  good: "Good",
  needs_attention: "Needs attention",
  critical: "Critical",
};

/** Bucket as text: critical gets a solid ink label, the rest plain words. */
function BucketLabel({ bucket }: { bucket: RepoAuditBucket }) {
  if (bucket === "critical") {
    return (
      <span className={tbl.flag} data-tone="solid">
        Critical
      </span>
    );
  }
  return (
    <span className={styles.bucketText} data-bucket={bucket}>
      {BUCKET_LABEL[bucket]}
    </span>
  );
}

const CHECK_WORD: Record<RepoAuditCheck["status"], string> = {
  pass: "pass",
  fail: "fail",
  warn: "warn",
  unknown: "?",
};

function CheckStatus({ status }: { status: RepoAuditCheck["status"] }) {
  return (
    <span className={styles.checkStatus} data-status={status}>
      {CHECK_WORD[status] ?? status}
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "–";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function relativeTime(iso: string | null): string {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function SortTh({
  k,
  label,
  num,
  sort,
  order,
  onSort,
}: {
  k: SortKey;
  label: string;
  num?: boolean;
  sort: SortKey;
  order: "asc" | "desc";
  onSort: (key: SortKey) => void;
}) {
  return (
    <th
      data-num={num || undefined}
      aria-sort={
        sort === k ? (order === "asc" ? "ascending" : "descending") : undefined
      }
    >
      <button type="button" onClick={() => onSort(k)}>
        {label}
        {sort === k ? (order === "asc" ? " ↑" : " ↓") : ""}
      </button>
    </th>
  );
}

export function RepoAuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const bucket = (searchParams.get("bucket") as BucketFilter) || "all";
  const staleOnly = searchParams.get("stale") === "1";
  const offConventionOnly = searchParams.get("offConvention") === "1";
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("score");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const setParam = (key: string, value: string | null) =>
    setSearchParams((prev) => {
      if (value === null) prev.delete(key);
      else prev.set(key, value);
      return prev;
    });

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const { data: response, isLoading } = useQuery({
    queryKey: ["repo-audit"],
    queryFn: () => api.get<{ data: RepoAuditResponse }>("/repo-audit"),
    staleTime: 60 * 1000,
  });

  const { data: statusResp } = useQuery({
    queryKey: ["repo-audit-status"],
    queryFn: () => api.get<{ data: RepoAuditStatus }>("/repo-audit/status"),
    refetchInterval: (query) => (query.state.data?.data.running ? 3000 : false),
  });
  const isRunning = statusResp?.data.running ?? false;

  // When a run finishes, pull in the fresh results.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !isRunning) {
      queryClient.invalidateQueries({ queryKey: ["repo-audit"] });
    }
    wasRunning.current = isRunning;
  }, [isRunning, queryClient]);

  const refreshMutation = useMutation({
    mutationFn: () => api.post<unknown>("/repo-audit/refresh"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["repo-audit-status"] });
    },
  });

  const summary = response?.data.summary;
  const allRepos = useMemo(() => response?.data.repos ?? [], [response]);

  const filtered = useMemo(() => {
    let rows = allRepos;
    if (bucket !== "all") rows = rows.filter((r) => r.bucket === bucket);
    if (staleOnly) rows = rows.filter((r) => r.isStale);
    if (offConventionOnly) rows = rows.filter((r) => !r.namingOk);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.fullName.toLowerCase().includes(q),
      );
    }
    const sorted = [...rows].sort((a, b) => {
      let cmp = 0;
      if (sort === "score") cmp = a.score - b.score;
      else if (sort === "name") cmp = a.name.localeCompare(b.name);
      else if (sort === "lastPushedAt") {
        const at = a.lastPushedAt ? new Date(a.lastPushedAt).getTime() : 0;
        const bt = b.lastPushedAt ? new Date(b.lastPushedAt).getTime() : 0;
        cmp = at - bt;
      }
      return order === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [allRepos, bucket, staleOnly, offConventionOnly, search, sort, order]);

  const handleSort = (key: SortKey) => {
    if (sort === key) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setSort(key);
      // Worst-first defaults: low score / oldest push first; names A to Z.
      setOrder("asc");
    }
  };

  const sortProps = { sort, order, onSort: handleSort };

  return (
    <>
      <div className={ed.kicker}>
        Repo audit · as of {relativeTime(summary?.lastAuditedAt ?? null)}
      </div>
      <h1 className={ed.headlineCompact}>
        {summary
          ? repoHeadline(
              summary.totalRepos,
              summary.critical,
              summary.needsAttention,
            )
          : "Repo audit"}
      </h1>
      {summary && summary.totalRepos > 0 && (
        <p className={ed.dek}>
          {repoDek(summary.stale, summary.offConvention, summary.avgScore)}
        </p>
      )}
      <div className={styles.bylineRow}>
        <div className={ed.byline} style={{ marginBottom: 0, flex: 1 }}>
          <b>Repo audit</b> · branch protection, CI, naming, freshness and docs,
          rolled into one score out of 100. Worst first. Last run{" "}
          <span className="num">
            {relativeTime(summary?.lastAuditedAt ?? null)}
          </span>
          .
        </div>
        <button
          type="button"
          className={btn.primary}
          onClick={() => refreshMutation.mutate()}
          disabled={isRunning || refreshMutation.isPending}
        >
          {isRunning ? "Auditing…" : "Run audit now"}
        </button>
      </div>

      <Ledger
        hideMissingPrior
        columns="fit"
        items={[
          {
            label: "Repositories",
            value: fmtFixed(summary?.totalRepos),
            note: summary
              ? `average score ${fmtFixed(summary.avgScore)}`
              : undefined,
          },
          { label: "Good", value: fmtFixed(summary?.good), note: "score 80+" },
          {
            label: "Needs attention",
            value: fmtFixed(summary?.needsAttention),
            note: "50 to 79",
          },
          {
            label: "Critical",
            value: fmtFixed(summary?.critical),
            note: "under 50",
            flag: summary && summary.critical > 0 ? "caution" : undefined,
          },
          {
            label: "Stale",
            value: fmtFixed(summary?.stale),
            note: "no push in 90 days",
          },
          {
            label: "Off-convention",
            value: fmtFixed(summary?.offConvention),
            note: "name prefix",
          },
        ]}
      />

      <div className={ed.sect}>The repositories</div>
      <h2 className={ed.h2}>Every repository, lowest score first</h2>
      <div className={tbl.controls}>
        <input
          type="search"
          placeholder="Search repos"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={field.search}
          aria-label="Search repositories"
        />
        <select
          value={bucket}
          onChange={(e) =>
            setParam("bucket", e.target.value === "all" ? null : e.target.value)
          }
          className={field.select}
          aria-label="Bucket"
        >
          <option value="all">All buckets</option>
          <option value="critical">Critical</option>
          <option value="needs_attention">Needs attention</option>
          <option value="good">Good</option>
        </select>
        <button
          type="button"
          className={btn.outline}
          aria-pressed={staleOnly}
          onClick={() => setParam("stale", staleOnly ? null : "1")}
        >
          Stale
        </button>
        <button
          type="button"
          className={btn.outline}
          aria-pressed={offConventionOnly}
          onClick={() =>
            setParam("offConvention", offConventionOnly ? null : "1")
          }
        >
          Off-convention
        </button>
        <span className={ed.num}>
          {fmtFixed(filtered.length)} of {fmtFixed(allRepos.length)}
        </span>
      </div>

      {isLoading ? (
        <div className={ed.loading}>Loading repo audit…</div>
      ) : allRepos.length === 0 ? (
        <p className={ed.proseLinked}>
          No audit data yet. The audit runs daily and on startup; run it now
          with the button above. Connect a GitHub org in Settings if you haven't
          already.
        </p>
      ) : (
        <div className={tbl.scroll}>
          <table className={tbl.numeric}>
            <thead>
              <tr>
                <th aria-label="Expand" />
                <SortTh k="name" label="Repository" {...sortProps} />
                <SortTh k="score" label="Score" num {...sortProps} />
                <th>Bucket</th>
                <th>Default branch</th>
                <th>Protected</th>
                <th>CI</th>
                <SortTh k="lastPushedAt" label="Last push" {...sortProps} />
                <th>Naming</th>
                <th>Visibility</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((repo) => {
                const open = expanded.has(repo.id);
                return (
                  <React.Fragment key={repo.id}>
                    <tr>
                      <td className={styles.expandCell}>
                        <button
                          type="button"
                          onClick={() => toggleExpand(repo.id)}
                          className={styles.expandButton}
                          aria-expanded={open}
                          aria-label={open ? "Hide checks" : "Show checks"}
                        >
                          {open ? "−" : "+"}
                        </button>
                      </td>
                      <td data-text className={styles.repo}>
                        <a href={repo.htmlUrl} target="_blank" rel="noreferrer">
                          {repo.name}
                        </a>
                        <span className={styles.org}>{repo.orgName}</span>
                        {repo.auditError && (
                          <div className={styles.auditError}>
                            {repo.auditError}
                          </div>
                        )}
                      </td>
                      <td data-num className={styles.score}>
                        {repo.score}
                      </td>
                      <td data-text>
                        <BucketLabel bucket={repo.bucket} />
                      </td>
                      <td>
                        {repo.defaultBranch}
                        {!repo.defaultBranchOk && (
                          <span
                            className={tbl.flag}
                            title="Default branch isn't main"
                          >
                            Off
                          </span>
                        )}
                      </td>
                      <td data-text>
                        {repo.branchProtected === null ? (
                          <span data-dim>unknown</span>
                        ) : repo.branchProtected ? (
                          "yes"
                        ) : (
                          <span className={tbl.flag}>No</span>
                        )}
                      </td>
                      <td>
                        <CiCell repo={repo} />
                      </td>
                      <td>
                        {formatDate(repo.lastPushedAt)}
                        {repo.isStale && <span className={tbl.flag}>Stale</span>}
                      </td>
                      <td>
                        {repo.namingOk ? (
                          <span title={`Matches "${repo.matchedPrefix}"`}>
                            {repo.matchedPrefix}
                          </span>
                        ) : (
                          <span
                            className={tbl.flag}
                            title="Name doesn't start with a product prefix"
                          >
                            Off
                          </span>
                        )}
                      </td>
                      <td data-text className={styles.dim}>
                        {repo.visibility}
                      </td>
                    </tr>
                    {open && <ExpandedChecks repo={repo} colSpan={10} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className={ed.loading}>No repos match these filters.</div>
          )}
        </div>
      )}
      <p className={ed.methodNarrow}>
        Score is the weighted share of applicable checks that pass; checks the
        GitHub App can't see are left out rather than counted as failures. Good
        is 80 and up, critical below 50. Stale means no push in 90 days. Naming
        expects a product prefix (aries, tops, towpay, towlien, da and others).
      </p>
    </>
  );
}

function CiCell({ repo }: { repo: RepoAuditRow }) {
  const ciCheck = repo.checks.find((c) => c.id === "ci_passing");
  if (!repo.hasCi) {
    return <span className={tbl.flag}>None</span>;
  }
  const status = ciCheck?.status ?? "unknown";
  return (
    <span title={ciCheck?.detail ?? repo.lastCiStatus ?? ""}>
      <CheckStatus status={status} />
      {repo.lastCiStatus && (
        <span className={styles.ciDetail}> {repo.lastCiStatus}</span>
      )}
    </span>
  );
}

function ExpandedChecks({
  repo,
  colSpan,
}: {
  repo: RepoAuditRow;
  colSpan: number;
}) {
  const failing = repo.failingChecks;
  const unknown = repo.checks.filter((c) => c.status === "unknown");
  return (
    <tr className={styles.expandedRow}>
      <td colSpan={colSpan} className={styles.expandedCell}>
        <div className={styles.checksHeading}>
          {failing.length === 0
            ? "All scored checks passing"
            : `Failing checks (${failing.length})`}
        </div>
        <dl className={styles.checksList}>
          {failing.map((check) => (
            <div key={check.id} className={styles.checkRow}>
              <dt>
                <CheckStatus status={check.status} /> {check.label}
              </dt>
              <dd>{check.detail}</dd>
            </div>
          ))}
        </dl>
        {unknown.length > 0 && (
          <>
            <div className={styles.checksHeading}>
              Not evaluated (excluded from score)
            </div>
            <dl className={styles.checksList}>
              {unknown.map((check) => (
                <div key={check.id} className={styles.checkRow}>
                  <dt>
                    <CheckStatus status={check.status} /> {check.label}
                  </dt>
                  <dd>{check.detail}</dd>
                </div>
              ))}
            </dl>
          </>
        )}
      </td>
    </tr>
  );
}

const styles = {
  bylineRow: css({
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    mb: "20px",
    flexWrap: { base: "wrap", md: "nowrap" },
  }),
  bucketText: css({
    fontFamily: "sans",
    fontSize: "12px",
    color: "ed.ink2",
    "&[data-bucket=needs_attention]": { color: "ed.ink", fontWeight: 600 },
  }),
  checkStatus: css({
    fontFamily: "mono",
    fontSize: "11px",
    textTransform: "uppercase",
    "&[data-status=fail]": { fontWeight: 700, color: "ed.ink" },
    "&[data-status=warn]": { fontWeight: 600, color: "ed.ink" },
    "&[data-status=pass]": { color: "ed.ink3" },
    "&[data-status=unknown]": { color: "ed.ink3" },
  }),
  ciDetail: css({ color: "ed.ink3", fontSize: "11px" }),
  expandCell: css({ width: "24px", px: "2px!" }),
  expandButton: css({
    all: "unset",
    cursor: "pointer",
    fontFamily: "mono",
    fontSize: "13px",
    width: "18px",
    height: "18px",
    lineHeight: "16px",
    textAlign: "center",
    border: "1px solid",
    borderColor: "ed.rule",
    color: "ed.ink",
    _hover: { borderColor: "ed.ink" },
    _focusVisible: { outline: "1px solid var(--colors-ed-ink)" },
  }),
  repo: css({ whiteSpace: "nowrap!", "& a": { fontWeight: 600 } }),
  org: css({ ml: "6px", fontSize: "10.5px", color: "ed.ink3" }),
  auditError: css({
    display: "inline-block",
    mt: "2px",
    fontSize: "11px",
    color: "ed.ink",
    bg: "ed.cautionTint",
    px: "4px",
    whiteSpace: "normal",
  }),
  score: css({ fontWeight: 600, fontSize: "13px!" }),
  dim: css({ color: "ed.ink3!", textTransform: "lowercase" }),
  expandedRow: css({ "&:hover td": { bg: "ed.panel!" } }),
  expandedCell: css({
    bg: "ed.panel",
    px: "36px!",
    py: "10px!",
    whiteSpace: "normal!",
    fontFamily: "sans!",
  }),
  checksHeading: css({
    fontFamily: "sans",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink2",
    mb: "4px",
    "&:not(:first-child)": { mt: "10px" },
  }),
  checksList: css({
    m: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  }),
  checkRow: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", md: "280px 1fr" },
    gap: "2px 16px",
    fontSize: "12.5px",
    "& dt": { fontWeight: 600, color: "ed.ink" },
    "& dd": { m: 0, color: "ed.ink2", fontSize: "12px" },
  }),
};
